import React, { useState } from "react";
import { toast } from "sonner";
import { Field, Input, Textarea, Select, Checkbox, RadioPills, SubmitButton } from "./FormPrimitives";
import { FormSuccess } from "./FormSuccess";
import { submitReferral } from "../../lib/api";

const initial = {
  referrer_name: "", organization: "", phone: "", email: "",
  veteran_first_name: "", veteran_last_name: "", veteran_situation: "",
  urgency: "", risks_needs: "", permission_to_contact: false, notes: "",
};

export const ReferralForm = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.referrer_name.trim()) e.referrer_name = "Please enter your name.";
    if (!form.phone.trim()) e.phone = "A phone number helps us coordinate.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.veteran_first_name.trim()) e.veteran_first_name = "Required.";
    if (!form.veteran_last_name.trim()) e.veteran_last_name = "Required.";
    if (!form.veteran_situation.trim()) e.veteran_situation = "Describe the veteran's situation.";
    if (!form.urgency) e.urgency = "Please select an urgency level.";
    if (!form.permission_to_contact) e.permission_to_contact = "Permission is required to proceed.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please review the highlighted fields."); return; }
    setLoading(true);
    try {
      const res = await submitReferral(form);
      setDone(res);
      toast.success("Referral submitted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <FormSuccess
        testid="referral-success"
        title="Referral received."
        message={done.message}
        refId={done.ref_id}
        emphasizeRef
        onReset={() => { setForm(initial); setDone(null); }}
      />
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 gap-6" data-testid="referral-form" noValidate>
      <div className="label-caps text-navy">Your details</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Your name" required htmlFor="referrer_name" error={errors.referrer_name}>
          <Input id="referrer_name" data-testid="referral-name" value={form.referrer_name} onChange={(e) => set("referrer_name", e.target.value)} placeholder="Full name" />
        </Field>
        <Field label="Organization" htmlFor="organization" hint="Agency, church, hospital — if any.">
          <Input id="organization" data-testid="referral-org" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="Optional" />
        </Field>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Phone" required htmlFor="ref_phone" error={errors.phone}>
          <Input id="ref_phone" data-testid="referral-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(000) 000-0000" />
        </Field>
        <Field label="Email" required htmlFor="ref_email" error={errors.email}>
          <Input id="ref_email" type="email" data-testid="referral-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
        </Field>
      </div>

      <div className="h-px bg-softgray my-2" />
      <div className="label-caps text-navy">The veteran</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Veteran first name" required htmlFor="v_first" error={errors.veteran_first_name}>
          <Input id="v_first" data-testid="referral-vfirst" value={form.veteran_first_name} onChange={(e) => set("veteran_first_name", e.target.value)} />
        </Field>
        <Field label="Veteran last name" required htmlFor="v_last" error={errors.veteran_last_name}>
          <Input id="v_last" data-testid="referral-vlast" value={form.veteran_last_name} onChange={(e) => set("veteran_last_name", e.target.value)} />
        </Field>
      </div>

      <Field label="Veteran's current situation" required htmlFor="v_situation" error={errors.veteran_situation}>
        <Textarea id="v_situation" data-testid="referral-situation" value={form.veteran_situation} onChange={(e) => set("veteran_situation", e.target.value)} placeholder="Housing status, recent events, anything relevant." />
      </Field>

      <Field label="Urgency level" required error={errors.urgency}>
        <RadioPills name="referral-urgency" options={["Low", "Moderate", "High", "Immediate"]} value={form.urgency} onChange={(v) => set("urgency", v)} />
      </Field>

      <Field label="Known risks or support needs" htmlFor="risks_needs">
        <Textarea id="risks_needs" data-testid="referral-risks" value={form.risks_needs} onChange={(e) => set("risks_needs", e.target.value)} placeholder="Optional — medical, mental health, safety, benefits, etc." />
      </Field>

      <Field label="Notes" htmlFor="ref_notes">
        <Textarea id="ref_notes" data-testid="referral-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional" />
      </Field>

      <Checkbox id="referral-permission" label="I have the veteran's permission for Iron Shield to contact them about this referral." checked={form.permission_to_contact} onChange={(e) => set("permission_to_contact", e.target.checked)} error={errors.permission_to_contact} />

      <div><SubmitButton loading={loading} testid="referral-submit">Refer a Veteran</SubmitButton></div>
    </form>
  );
};

export default ReferralForm;
